<template>
  <section class="py-[25px] w-full min-h-screen">
    <div class="flex flex-col md:flex-row gap-[10px] md:items-end justify-between">
      <div>
        <h3 class="text-[16px] font-bold text-[#353535] mb-[4px]">Welcome back {{ user.username }},</h3>
        <p class="text-[14px] font-normal text-[#96a0b5]">Here's how your products are performing!</p>
      </div>
      <div class="flex gap-0 border border-solid border-[#96a0b5] rounded-[8px] max-w-max w-full overflow-hidden">
        <button v-for="period in timePeriods" :key="period" :class="buttonClasses(period)" @click="selectPeriod(period)"
          class="text-[14px] font-medium px-[16px] py-[10px] border-none outline-none cursor-pointer transition-all duration-300 ease-in-out">
          {{ period }}
        </button>
      </div>
    </div>

    <!-- dash stats -->
    <div class="mt-[24px] grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-[16px]">
      <DashStatCard v-for="stat in dashStats" :key="stat.key" :label="stat.label"
        :value="dashData[stat.key] && dashData[stat.key].count || 0" />
    </div>

    <!-- donut and top products -->
    <div class="mt-[24px] grid lg:grid-cols-2 gap-[16px]">
      <div class="border border-[#EAECF0] rounded-[10px] pt-[20px] h-full">
        <h4 class="text-[20px] font-bold text-[#181D27] mb-[16px] px-[24px]">Order Breakdown</h4>
        <div class="flex items-center justify-center border-t border-[#EAECF0] bg-white p-[12px] md:p-[24px]">
          <apexchart v-if="hasValidSeriesData" type="donut" :height="chartHeight" :width="chartWidth"
            :options="orderBreakdownOptions" :series="series" class="w-full" />
          <div v-else class="text-[#96a0b5] text-[14px] py-[50px]">No data available</div>
        </div>
      </div>
      <!-- top selling products -->
      <div class="border border-[#EAECF0] rounded-[10px] pt-[20px] h-auto">
        <h4 class="text-[20px] font-bold text-[#181D27] mb-[16px] px-[24px]">Top Selling Products</h4>
        <div :class="[
          'flex items-center justify-center border-t border-[#EAECF0] overflow-x-auto overflow-y-auto bg-white',
          topProducts.length === 0 ? 'p-[12px] md:p-[24px]' : 'p-0'
        ]">
          <table class="w-full table m-0 dataTable no-footer min-w-[400px]" v-if="topProducts.length > 0">
            <thead class="bg-[#f6f6f6] sticky top-0">
              <tr class="border-b border-[#e5e7eb]">
                <th class="text-left text-[12px] font-medium text-[#000000] py-[12px] pl-[24px] whitespace-nowrap">
                  Product</th>
                <th class="text-right text-[12px] font-medium text-[#000000] py-[12px] whitespace-nowrap">Unit Sold</th>
                <th class="text-right text-[12px] font-medium text-[#000000] py-[12px] pr-[24px] whitespace-nowrap">
                  Amount</th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <tr v-for="(product, index) in topProducts" :key="index" class="border-b border-[#f3f4f6]">
                <td
                  class="text-[14px] font-medium text-[#353535] py-[12px] pl-[24px] max-w-[150px] md:max-w-none truncate">
                  {{ product.name }}
                </td>
                <td class="text-[14px] font-medium text-[#353535] py-[12px] text-right whitespace-nowrap">
                  {{ product.units }}
                </td>
                <td class="text-[14px] font-medium text-[#353535] py-[12px] text-right pr-[24px] whitespace-nowrap">
                  ₦{{ formatMoney(product.amount) }}
                </td>
              </tr>
            </tbody>
          </table>

          <div v-else class="text-[#96a0b5] text-[14px] py-[50px]">No data available</div>
        </div>

      </div>
    </div>

    <!-- sales performance -->
    <div class="mt-[24px] border border-[#EAECF0] rounded-[10px] pt-[14px]">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-[14px] px-[24px] gap-[12px]">
        <h4 class="text-[20px] font-bold text-[#181D27]">Sales Performance</h4>
        <select v-model="selectedYear" @change="loadSalesPerformance"
          class="border border-[#96a0b5] rounded-[8px] px-[16px] py-[10px] text-[14px] font-medium text-[#353535] outline-none cursor-pointer max-w-[150px]">
          <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
        </select>
      </div>
      <div class="border-t border-[#EAECF0] bg-white p-[12px] md:p-[24px]">
        <apexchart v-if="hasSalesData" type="bar" :height="salesChartHeight" :options="salesPerformanceOptions"
          :series="salesPerformanceSeries" />
        <div v-else class="text-[#96a0b5] text-[14px] text-center py-[50px]">No data available</div>
      </div>
    </div>

    <!-- recent activities -->
    <div class="mt-[24px] border border-[#EAECF0] rounded-[10px] pt-[20px]">
      <h4 class="text-[20px] font-bold text-[#181D27] mb-[16px] px-[24px]">Recent Activities</h4>
      <div class="border-t border-[#EAECF0] bg-white max-h-[400px] overflow-y-auto">
        <div v-if="recentActivities.length > 0" class="divide-y divide-[#f3f4f6]">
          <div v-for="activity in recentActivities.slice(0, 50)" :key="activity.id"
            class="px-[24px] py-[16px] hover:bg-[#f9fafb] transition-colors duration-200">
            <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-[8px] sm:gap-[16px]">
              <div class="flex-1">
                <div class="flex items-center gap-[8px] mb-[4px]">
                  <span class="text-[14px] font-semibold text-[#181D27]">{{
                    activity.activityType
                  }}</span>
                  <span class="text-[12px] text-[#96a0b5]">{{ formatActivityTime(activity.timestamp) }}</span>
                </div>
                <p class="text-[14px] text-[#535862]">
                  <span v-html="formatActivityDescription(activity)"></span> by {{ activity.actorName }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-[#96a0b5] text-[14px] text-center py-[50px]">No data available</div>
      </div>
    </div>


    <!-- mobile app download enforced for distributors -->
    <div class="modal bg-[rgba(0,0,0,0.8)] fade colored-header colored-header-primary" id="mod-subscription"
      role="dialog" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content p-3">
          <div class="text-center">
            <h2 class="text-2xl font-medium mb-[10px]">Hi {{ user.displayName }}</h2>
            <p class="text-base md:text-xl font-normal">
              Please Download the mobile app to get started
            </p>
            <button type="button" class="bg-[#2463EB] text-white py-[12px] rounded-sm w-100 mt-2" @click="logout">
              <span class="font-weight-bold">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/* eslint-disable */
import moment from "moment";
import { helper } from "@/helper";
import VueApexCharts from "vue-apexcharts";
import CreateBankAccount from "@/components/BankAccount/CreateBankAccount.vue";
import { color } from "d3";

const DashStatCard = {
  props: {
    label: String,
    value: [Number, String]
  },
  render: function (h) {
    return h('div', {
      class: 'border border-[#E9EAEB] rounded-[10px] p-[12px]'
    }, [
      h('p', {
        class: 'text-[#535862] font-medium text-[12px]'
      }, this.label),
      h('span', {
        class: 'text-[#181D27] font-semibold text-[24px]'
      }, this.value)
    ]);
  }
};

export default {
  name: "dashboard",
  created() {
    this.load();
    this.updateChartDimensions();
    this.initializeYears();
  },

  mounted() {
    if (process.env.NODE_ENV === 'production') {
      this.checkAndEnforceSubscriptionModal();
      if (
        this.user.type === "Distributor" &&
        this.user.role !== "Customer"
      ) {
        this.openSubModal();
      }
    }
    window.addEventListener('resize', this.updateChartDimensions);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateChartDimensions);
  },

  components: {
    apexchart: VueApexCharts,
    CreateBankAccount,
    DashStatCard
  },

  data() {
    return {
      isBusy: true,
      dashData: {},
      distributorNetwork: [],
      isSubscriptionModalForced: false,
      manufacturers: [],
      distributors: [],
      viewList: [],
      viewListType: "",
      series: [],
      chartHeight: 400,
      chartWidth: '100%',

      timePeriods: ['Day', 'Week', 'Month'],
      selectedPeriod: 'Week',

      selectedYear: new Date().getFullYear(),
      availableYears: [],
      salesPerformanceSeries: [],
      salesChartHeight: 350,
      recentActivities: [],

      dashStats: [
        { key: 'total', label: 'Total Orders' },
        { key: 'completed', label: 'Completed Orders' },
        { key: 'inProgress', label: 'Orders in Progress' },
        { key: 'awaitingApproval', label: 'Orders Awaiting Approval' },
        { key: 'rejected', label: 'Cancelled Orders' }
      ],
      orderBreakdownOptions: {
        chart: {
          type: 'donut',
        },
        labels: ['Completed', 'Processing', 'Pending Approval', 'Cancelled'],
        colors: ['#16A249', '#2463EB', '#FD881E', '#DC2828'],
        legend: {
          position: 'right',
          offsetY: 80,
          offsetX: 0,
          formatter: function (seriesName, opts) {
            const value = opts.w.globals.series[opts.seriesIndex];
            const total = opts.w.globals.series.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            const paddedName = seriesName.padEnd(50, ' ');
            return paddedName + percentage + '%';
          }
        },
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          enabled: true,
          theme: 'light',
          style: {
            fontSize: '12px',
            color: '#96a0b5'
          },
          fillSeriesColor: false,
          y: {
            formatter: function (value) {
              return '₦' + helper.formatMoney(value);
            }
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '16px',
                  color: '#353535',
                  formatter: function (w) {
                    const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(2);
                    return '₦' + helper.formatMoney(total);
                  }
                },
                value: {
                  color: '#353535',
                  formatter: function (val) {
                    return '₦' + helper.formatMoney(val);
                  }
                }
              }
            }
          }
        },
        responsive: [{
          breakpoint: 768,
          options: {
            chart: {
              height: 350
            },
            legend: {
              position: 'bottom',
              horizontalAlign: 'center',
              offsetY: 0,
              colors: '#FFFFFF',
              formatter: function (seriesName, opts) {
                const value = opts.w.globals.series[opts.seriesIndex];
                const total = opts.w.globals.series.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return seriesName + ': ' + percentage + '%';
              }
            }
          }
        }]
      },
      salesPerformanceOptions: {
        chart: {
          type: 'bar',
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            borderRadius: 8,
            columnWidth: '60%',
          }
        },
        dataLabels: {
          enabled: false
        },
        colors: ['#2463EB'],
        xaxis: {
          categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          labels: {
            style: {
              fontSize: '12px',
              colors: '#535862'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '12px',
              colors: '#535862'
            },
            formatter: function (val) {
              return '₦' + helper.formatMoney(val);
            }
          },
          title: {
            text: 'Sales Amount (₦)',
            style: {
              fontSize: '12px',
              fontWeight: 500,
              color: '#535862'
            }
          }
        },
        grid: {
          borderColor: '#f3f4f6',
          strokeDashArray: 4
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return '₦' + helper.formatMoney(val);
            }
          }
        }
      },
      seriesChannels: [],
      seriesTopProduct: [
        {
          name: "Sale Count",
          data: [],
        },
      ],
      seriesTop5groupsbyOrderCount: [
        {
          name: "Order Count",
          data: [],
        },
      ],
      seriesTop5groupsbyAmount: [
        {
          name: "Amount(₦)",
          data: [],
        },
      ],
      seriesTopProductLabels: [],
      showAccountBalance: false,
      accountDetails: {},
      grouData: null,
      pay4meStep: 1,
      nextStepMsg: "",
      pay4Status: null,
      pay4Limit: 0,
      payPhoneNumber: "",
      payUserId: "",
      isBusyPay4Me: false,
      payPhoneNumberInvalid: false,
      clientName: process.env.VUE_APP_CLIENT_NAME,
      topProducts: []
    };
  },

  computed: {
    user: function () {
      return this.$store.state.currentUser;
    },
    shouldForceSubscriptionModal() {
      return (
        this.user.type === "Distributor" &&
        this.user.role !== "Customer"
      );
    },
    distributorNetworkFiltered: function () {
      return this.getFilteredList(this.distributorNetwork);
    },
    manufacturersFiltered: function () {
      return this.getFilteredList(this.manufacturers);
    },
    distributorsFiltered: function () {
      return this.getFilteredList(this.distributors);
    },
    hasValidSeriesData: function () {
      if (!this.series || this.series.length === 0) {
        return false;
      }
      const hasData = this.series.some(function (val) {
        return typeof val === 'number' && !isNaN(val) && val > 0;
      });
      return hasData;
    },
    hasSalesData: function () {
      if (!this.salesPerformanceSeries || this.salesPerformanceSeries.length === 0) {
        return false;
      }
      const hasData = this.salesPerformanceSeries[0].data.some(function (val) {
        return typeof val === 'number' && !isNaN(val) && val > 0;
      });
      return hasData;
    }
  },

  methods: {
    initializeYears() {
      const currentYear = new Date().getFullYear();
      this.availableYears = [];
      for (let year = 2024; year <= currentYear; year++) {
        this.availableYears.push(year);
      }
      this.selectedYear = currentYear;
    },

    updateChartDimensions() {
      const width = window.innerWidth;
      if (width < 768) {
        this.chartHeight = 350;
        this.chartWidth = '100%';
      } else {
        this.chartHeight = 400;
        this.chartWidth = '100%';
      }
    },

    buttonClasses(period) {
      const isActive = this.selectedPeriod === period;
      const baseClasses = 'text-[14px] font-medium px-[16px] py-[12px] border-none outline-none cursor-pointer transition-all duration-300 ease-in-out';
      const activeClasses = isActive ? 'bg-[#7367F0] text-white' : 'text-[#414651] hover:bg-[#7367F0] hover:text-white';
      const middleButton = period === 'Week' ? 'border-l border-r border-[#96a0b5]' : '';
      return `${baseClasses} ${activeClasses} ${middleButton}`;
    },

    selectPeriod(period) {
      this.selectedPeriod = period;
      const dateRange = this.getDateRangeForPeriod(period);
      this.load(dateRange.startDate, dateRange.endDate);
    },

    getDateRangeForPeriod(period) {
      const today = new Date();
      let startDate, endDate;

      const formatDateTime = (date, isEndDate = false) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const time = isEndDate ? '23:59:59' : '00:00:00';
        return `${year}-${month}-${day} ${time}`;
      };

      switch (period) {
        case 'Day':
          startDate = new Date(today);
          endDate = new Date(today);
          break;

        case 'Week':
          const currentDay = today.getDay();
          const daysToMonday = currentDay === 0 ? 6 : currentDay - 1;
          const monday = new Date(today);
          monday.setDate(today.getDate() - daysToMonday);
          const sunday = new Date(monday);
          sunday.setDate(monday.getDate() + 6);
          startDate = monday;
          endDate = sunday;
          break;

        case 'Month':
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          startDate = firstDay;
          endDate = lastDay;
          break;

        default:
          const day = today.getDay();
          const toMonday = day === 0 ? 6 : day - 1;
          const mon = new Date(today);
          mon.setDate(today.getDate() - toMonday);
          const sun = new Date(mon);
          sun.setDate(mon.getDate() + 6);
          startDate = mon;
          endDate = sun;
      }

      return {
        startDate: formatDateTime(startDate),
        endDate: formatDateTime(endDate, true)
      };
    },

    getFilteredList(list) {
      if (list.length > 5) {
        let clone = list.slice();
        return clone.splice(0, 5);
      }
      return list;
    },

    getValidNumber(value) {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    },

    checkAndEnforceSubscriptionModal() {
      if (this.shouldForceSubscriptionModal) {
        this.isSubscriptionModalForced = true;
        this.openSubModal();
        $("#mod-subscription").on("hide.bs.modal", (e) => {
          if (this.isSubscriptionModalForced) {
            e.preventDefault();
            this.openSubModal();
          }
        });
        this.monitorModalState();
      }
    },

    monitorModalState() {
      const modal = $("#mod-subscription");
      const observer = new MutationObserver(() => {
        if (
          this.isSubscriptionModalForced &&
          !modal.hasClass("show")
        ) {
          this.openSubModal();
        }
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    },

    logout: function () {
      this.$auth.destroyToken();
      this.$store.commit("clearCurrentUser");
      this.$store.replaceState({});
      this.closeSubModal();
      this.$router.push("/login");
    },

    async load(startDate = null, endDate = null) {
      if (!startDate || !endDate) {
        const dateRange = this.getDateRangeForPeriod(this.selectedPeriod);
        startDate = dateRange.startDate;
        endDate = dateRange.endDate;
      }

      try {
        const response = await this.$http.get(
          `/Reports/getDashboardReport?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
        );
        this.dashData = response.body;
        this.series = [
          this.getValidNumber((response.body.completed && response.body.completed.amount) || response.body.completed || 0),
          this.getValidNumber((response.body.inProgress && response.body.inProgress.amount) || response.body.inProgress || 0),
          this.getValidNumber((response.body.awaitingApproval && response.body.awaitingApproval.amount) || response.body.awaitingApproval || 0),
          this.getValidNumber((response.body.rejected && response.body.rejected.amount) || response.body.rejected || response.body.canceled || 0)
        ];
        this.topProducts = response.body.topProducts || [];
      } catch (error) {
        console.error("Error fetching dashboard report:", error);
      }
      await this.loadSalesPerformance();
      await this.loadRecentActivities();
      this.isBusy = false;
    },

    async loadSalesPerformance() {
      try {
        const response = await this.$http.get(
          `/Reports/sales-performance/${this.selectedYear}`
        );

        const monthlyData = new Array(12).fill(0);

        if (response.body && response.body.monthlyData && Array.isArray(response.body.monthlyData)) {
          response.body.monthlyData.forEach(item => {
            if (item.month >= 1 && item.month <= 12) {
              monthlyData[item.month - 1] = item.amount || 0;
            }
          });
        }

        this.salesPerformanceSeries = [{
          name: 'Sales Amount',
          data: monthlyData
        }];
      } catch (error) {
        console.error("Error fetching sales performance:", error);
        this.salesPerformanceSeries = [{
          name: 'Sales Amount',
          data: new Array(12).fill(0)
        }];
      }
    },

    async loadRecentActivities() {
      try {
        const response = await this.$http.get('/Reports/recent-activities');
        this.recentActivities = response.body || [];
      } catch (error) {
        console.error("Error fetching recent activities:", error);
        this.recentActivities = [];
      }
    },

    formatActivityTime(timestamp) {
      const activityDate = moment(timestamp);
      const now = moment();
      const diffMinutes = now.diff(activityDate, 'minutes');
      const diffHours = now.diff(activityDate, 'hours');
      const diffDays = now.diff(activityDate, 'days');

      if (diffMinutes < 1) {
        return 'Just now';
      } else if (diffMinutes < 60) {
        return `${diffMinutes} min${diffMinutes > 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        return activityDate.format('hh:mm A');
      } else if (diffDays === 1) {
        return 'Yesterday, ' + activityDate.format('h:mm A');
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return activityDate.format('MMM D, YYYY');
      }
    },
    formatActorName(actorName) {
      let actor = activity.actorName
    },
    formatActivityDescription(activity) {
      let description = activity.description;

      if (activity.orderNumber) {
        description = description.replace(
          activity.orderNumber,
          `<span class="text-[#2463EB] font-medium">${activity.orderNumber}</span>`
        );
      }

      if (activity.amount && activity.amount > 0) {
        const formattedAmount = '₦' + this.formatMoney(activity.amount);
        description = description.replace(
          /₦[\d,]+/g,
          `<span class="text-[#2463EB] font-semibold">${formattedAmount}</span>`
        );
      }

      return description;
    },

    formatDate(date) {
      return moment(date).format("ll");
    },

    formatMoney(val) {
      return helper.formatMoney(val);
    },

    openSubModal() {
      $("#mod-subscription").modal({
        backdrop: "static",
        keyboard: false,
      });
      $("#mod-subscription").modal("show");
    },
    closeSubModal() {
      const modal = $("#mod-subscription");
      modal.modal("hide");
      $(document.body).removeClass("modal-open");
      $(".modal-backdrop").remove();
      modal.off("hide.bs.modal");
    },
  },
};
</script>